import React, { useState } from 'react';
import {
  AppBar,
  Toolbar,
  Typography,
  Box,
  IconButton,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Divider,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import {
  Menu as MenuIcon,
  Close as CloseIcon,
  DarkMode,
  LightMode,
  Instagram as InstagramIcon,
  MailOutline,
} from '@mui/icons-material';
import { Link, useLocation } from 'react-router-dom';
import { useLanguage } from '../../shared/contexts/LanguageContext';
import { useLocalizedPath } from '../../shared/hooks/useLocalizedPath';
import { useNavItems } from '../../shared/hooks/useNavItems';
import { stripLanguage } from '../../shared/constants/routes';
import { useThemeMode } from '../../shared/contexts/ThemeContext';
import { ContentUseCase } from '../../corelogic/usecases/ContentUseCase';
import { LanguageSwitcher } from './LanguageSwitcher';
import { Footer } from './Footer';
import { Logo } from './Logo';

const contentUseCase = new ContentUseCase();

export const Layout: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const location = useLocation();
  const { language, t } = useLanguage();
  const lp = useLocalizedPath();
  const navItems = useNavItems();
  const { instagram } = contentUseCase.getSocialMediaContent(language);
  const { email } = contentUseCase.getOrgInfo(language);
  const { path: currentPath } = stripLanguage(location.pathname);
  const { mode, toggleMode, darkModeEnabled } = useThemeMode();
  // An editorial decision at /admin, like the dark view. The English pages stay
  // reachable either way - this is the control, not the language.
  const languageSwitcherEnabled = contentUseCase.isLanguageSwitcherEnabled();

  const closeDrawer = () => setMobileOpen(false);

  /**
   * The two ways off the site that are not a page: Instagram and the inbox.
   *
   * As symbols, not menu entries. They are not part of the reading order of
   * the site - a visitor picks them when they have finished with the page and
   * want to reach us - and set in words next to the nav they would compete
   * with it at the exact size the nav is trying to hold. The footer carries
   * the same pair; the header is where a visitor looks first, and expecting
   * them to scroll a whole page to find an address is a tax on the readers
   * this site is for.
   */
  const socialLinks = (
    <Box sx={{ display: 'flex', alignItems: 'center' }}>
      <IconButton
        component="a"
        href={instagram.url}
        target="_blank"
        rel="noopener noreferrer me"
        aria-label={`Instagram ${instagram.handle}`}
        sx={{ color: 'text.secondary', '&:hover': { color: 'primary.dark', backgroundColor: 'action.hover' } }}
      >
        <InstagramIcon />
      </IconButton>
      <IconButton
        component="a"
        href={`mailto:${email}`}
        aria-label={email}
        sx={{ color: 'text.secondary', '&:hover': { color: 'primary.dark', backgroundColor: 'action.hover' } }}
      >
        <MailOutline />
      </IconButton>
    </Box>
  );

  const themeToggle = darkModeEnabled && (
    <IconButton
      onClick={toggleMode}
      color="inherit"
      aria-label={mode === 'dark' ? t('theme.toLight') : t('theme.toDark')}
      sx={{ '&:hover': { backgroundColor: 'action.hover' } }}
    >
      {mode === 'dark' ? <LightMode /> : <DarkMode />}
    </IconButton>
  );

  const drawer = (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <Box
        sx={{
          p: 2,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 1,
        }}
      >
        <Box component={Link} to={lp('/')} onClick={closeDrawer} sx={{ textDecoration: 'none' }}>
          <Logo variant="compact" size={34} />
        </Box>
        <IconButton onClick={closeDrawer} aria-label={t('nav.close')} color="inherit">
          <CloseIcon />
        </IconButton>
      </Box>
      <Divider />
      <List component="nav" sx={{ flexGrow: 1, py: 1 }}>
        {navItems.map((item) => {
          const isActive = currentPath === item.path;
          return (
            <ListItem key={item.key} disablePadding>
              <ListItemButton
                component={Link}
                to={lp(item.path)}
                onClick={closeDrawer}
                aria-current={isActive ? 'page' : undefined}
                sx={{ py: 1.5, px: 3 }}
              >
                <ListItemText
                  primary={item.label}
                  slotProps={{
                    primary: {
                      sx: {
                        color: isActive ? 'primary.dark' : 'text.primary',
                        fontWeight: isActive ? 600 : 400,
                      },
                    },
                  }}
                />
              </ListItemButton>
            </ListItem>
          );
        })}
      </List>
      <Divider />
      <Box sx={{ p: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}>
        {socialLinks}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          {languageSwitcherEnabled && <LanguageSwitcher mobile />}
          {themeToggle}
        </Box>
      </Box>
    </Box>
  );

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
      {/* Keyboard users reach the content without tabbing the whole menu. */}
      <Box
        component="a"
        href="#main"
        sx={{
          position: 'absolute',
          left: -9999,
          top: 0,
          zIndex: (t2) => t2.zIndex.tooltip + 1,
          px: 2,
          py: 1.5,
          backgroundColor: 'background.paper',
          color: 'text.primary',
          border: '2px solid',
          borderColor: 'primary.dark',
          borderRadius: 1,
          '&:focus': { left: 8, top: 8 },
        }}
      >
        {t('nav.skip')}
      </Box>

      <AppBar
        position="sticky"
        elevation={0}
        sx={{
          backgroundColor: 'background.default',
          color: 'text.primary',
          borderRadius: 0,
          borderBottom: '1px solid',
          borderColor: 'divider',
        }}
      >
        <Toolbar
          sx={{
            px: { xs: 2, md: 4 },
            // The wordmark is only ~15px tall here, so the header's height is
            // set by the nav links (~45px) and the icon buttons (48px), not
            // by the logo. 56/64 is the tightest that still clears both.
            minHeight: { xs: '56px !important', md: '64px !important' },
            gap: 2,
          }}
        >
          <Box
            component={Link}
            to={lp('/')}
            aria-label="M.E.-Kollektiv"
            sx={{ flexGrow: 1, textDecoration: 'none', color: 'inherit', display: 'flex', alignItems: 'center', minWidth: 0 }}
          >
            <Logo variant="compact" size={isMobile ? 34 : 44} />
          </Box>

          {isMobile ? (
            <IconButton
              color="inherit"
              aria-label={t('nav.menu')}
              aria-expanded={mobileOpen}
              edge="end"
              onClick={() => setMobileOpen(true)}
            >
              <MenuIcon />
            </IconButton>
          ) : (
            <Box component="nav" sx={{ display: 'flex', gap: { md: 0.25, lg: 1 }, alignItems: 'center' }}>
              {navItems.map((item) => {
                const isActive = currentPath === item.path;
                return (
                  <Typography
                    key={item.key}
                    component={Link}
                    to={lp(item.path)}
                    aria-current={isActive ? 'page' : undefined}
                    sx={{
                      fontSize: '0.9375rem',
                      color: isActive ? 'primary.dark' : 'text.primary',
                      fontWeight: isActive ? 600 : 500,
                      textDecoration: 'none',
                      px: { md: 1, lg: 1.5 },
                      py: 1,
                      borderRadius: 1,
                      whiteSpace: 'nowrap',
                      borderBottom: '2px solid',
                      borderColor: isActive ? 'primary.main' : 'transparent',
                      '&:hover': { color: 'primary.dark', backgroundColor: 'action.hover' },
                    }}
                  >
                    {item.label}
                  </Typography>
                );
              })}
              {socialLinks}
              {themeToggle}
              {languageSwitcherEnabled && <LanguageSwitcher />}
            </Box>
          )}
        </Toolbar>
      </AppBar>

      <Drawer
        variant="temporary"
        open={mobileOpen}
        onClose={closeDrawer}
        ModalProps={{ keepMounted: true }}
        anchor="right"
        sx={{
          display: { xs: 'block', md: 'none' },
          '& .MuiDrawer-paper': { boxSizing: 'border-box', width: 288 },
        }}
      >
        {drawer}
      </Drawer>

      <Box component="main" id="main" sx={{ flexGrow: 1 }}>
        {children}
      </Box>
      <Footer />
    </Box>
  );
};
